/**
 * 장치 대장 '최근 수신' — 최근 14일 업로드를 주기(TDC) 칸으로 나눠 한 줄 띠로 보여 준다 (2026-11-04).
 *
 * '마지막 업링크' 한 칸만으로는 장치끼리 비교가 안 된다(모두 08:00 이면 똑같아 보인다). 띠는 '언제 · 얼마나 자주
 * 늦었고 빠졌는가' 를 한눈에 보여 주고, 옆 숫자(최근 간격 · 최대 간격 · 누락)로 정렬해 문제 장치를 위로 올린다.
 *
 * 칸 판정 (칸 = 지금부터 거꾸로 TDC 길이씩):
 *   정상   칸 안에 업로드가 있고 직전 업로드와의 간격 ≤ TDC + 1시간           — 채운 초록
 *   지연   간격 > TDC + 1시간 (재시도 · 망 접속 지연)                            — 채운 주황
 *   재전송 칸 안에 업로드가 2번 이상 (앞 칸의 못 받은 패킷을 REDPT 가 다시 보냄) — 채운 파랑
 *   누락   칸 안에 업로드가 없다                                                  — 빈 칸 + 빨간 테두리 (모양으로도 구분)
 *   이전   첫 업로드보다 앞 (설치 전)                                             — 옅은 회색 낮은 칸
 * 수신 등급(정상/지연/미수신)과 같은 경계(TDC + 1h)를 쓴다 — 백엔드 _level 과 어긋나지 않게.
 */
import React from 'react';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import { gray } from '../theme/tokens';
import { batteryGrade, signalGrade } from './ThLoggerBadges';

export type RxItem = [string, number, number];   // [received_at, n_records, n_inserted]
type Cell = { k: 'ok' | 'late' | 'retry' | 'miss' | 'pre'; from: number; to: number; ups: number[]; gapH?: number };
export type RxSummary = { cells: Cell[]; lastGapH: number | null; maxGapH: number | null; miss: number; late: number; retry: number; onTime: number | null };

const GRACE = 3600_000;

export const summarize = (items: RxItem[] | undefined, tdcSec: number, nowIso: string, days = 14): RxSummary => {
  const tdc = Math.max(tdcSec || 86400, 600) * 1000;
  const now = dayjs(nowIso).valueOf();
  const n = Math.min(60, Math.max(1, Math.floor((days * 86400_000) / tdc)));
  const ts = (items || []).map(x => dayjs(x[0]).valueOf()).sort((a, b) => a - b);
  const first = ts[0];
  const cells: Cell[] = [];
  for (let i = n; i >= 1; i--) {
    const from = now - i * tdc, to = from + tdc;
    const ups = ts.filter(t => t >= from && t < to);
    let k: Cell['k'];
    let gapH: number | undefined;
    if (!ups.length) k = first == null || to <= first ? 'pre' : 'miss';
    else {
      const prev = ts.filter(t => t < ups[0]).pop();
      gapH = prev != null ? (ups[0] - prev) / 3600_000 : undefined;
      k = ups.length >= 2 ? 'retry' : prev != null && ups[0] - prev > tdc + GRACE ? 'late' : 'ok';
    }
    cells.push({ k, from, to, ups, gapH });
  }
  const win = ts.filter(t => t >= now - n * tdc);
  const gaps: number[] = [];
  for (let i = 1; i < ts.length; i++) if (ts[i] >= now - n * tdc) gaps.push((ts[i] - ts[i - 1]) / 3600_000);
  // 진행 중인 공백도 간격이다 — 마지막 업로드 뒤로 지금까지가 TDC 를 넘었으면 최대 간격에 넣는다
  if (ts.length) gaps.push((now - ts[ts.length - 1]) / 3600_000);
  const real = cells.filter(c => c.k !== 'pre');
  return {
    cells,
    lastGapH: ts.length >= 2 ? (ts[ts.length - 1] - ts[ts.length - 2]) / 3600_000 : null,
    maxGapH: gaps.length ? Math.max(...gaps) : null,
    miss: cells.filter(c => c.k === 'miss').length,
    late: cells.filter(c => c.k === 'late').length,
    retry: cells.filter(c => c.k === 'retry').length,
    onTime: real.length ? Math.round((real.filter(c => c.k === 'ok').length / real.length) * 100) : (win.length ? 100 : null),
  };
};

const CELL: Record<Cell['k'], { bg: string; border: string; h: string; label: string }> = {
  ok:    { bg: '#1baf7a', border: '#1baf7a', h: '100%', label: '정상' },
  late:  { bg: '#eda100', border: '#eda100', h: '100%', label: '지연' },
  retry: { bg: '#2a78d6', border: '#2a78d6', h: '100%', label: '재전송' },
  miss:  { bg: '#fff', border: '#e5484d', h: '100%', label: '누락' },
  pre:   { bg: gray[200], border: gray[200], h: '35%', label: '설치 전' },
};
const fmtH = (h: number | null | undefined) => (h == null ? '-' : h >= 48 ? `${(h / 24).toFixed(1)}일` : `${h.toFixed(1)}h`);

// 짧은 시간 표기 — 한 줄에 넣으려고 10시간 이상은 소수점을 뺀다(48h · 3.0일)
const fmtS = (h: number | null | undefined) => (h == null ? '-' : h >= 48 ? `${(h / 24).toFixed(1)}일` : h >= 10 ? `${Math.round(h)}h` : `${h.toFixed(1)}h`);

/* 띠 + 요약을 한 줄로(2026-11-05 사용자 요청: '최근 업로드' 칸 3줄 → 2줄).
   위 줄은 업로드 박스(FreshCell), 이 줄은 [14일 띠] 최대 간격 · 누락 · 지연. '최근 간격' 은 요약에 마우스를 올리면 보인다 */
export const ReceptionStrip: React.FC<{ s: RxSummary; tdcSec: number }> = ({ s, tdcSec }) => {
  const tdcH = (tdcSec || 86400) / 3600;
  const warnGap = (h: number | null) => h != null && h > tdcH + 1;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 1.5, height: 14, alignItems: 'flex-end', width: 96, flex: 'none' }} role="img"
        aria-label={`최근 수신 — 누락 ${s.miss} · 지연 ${s.late} · 재전송 ${s.retry}`}>
        {s.cells.map((c, i) => (
          <Tooltip key={i} title={<div style={{ fontSize: 12 }}>
            <b>{CELL[c.k].label}</b> · {dayjs(c.from).format('MM-DD HH:mm')} ~ {dayjs(c.to).format('MM-DD HH:mm')}
            {c.ups.map((u, j) => <div key={j}>수신 {dayjs(u).format('MM-DD HH:mm')}</div>)}
            {c.gapH != null && <div>직전 업로드와 {fmtH(c.gapH)}</div>}
          </div>}>
            <span style={{ flex: 1, minWidth: 2, height: CELL[c.k].h, background: CELL[c.k].bg, border: `1.5px solid ${CELL[c.k].border}`, borderRadius: 2, boxSizing: 'border-box' }} />
          </Tooltip>))}
      </div>
      <Tooltip title={<div style={{ fontSize: 12 }}>최근 14일 · 최근 간격 {fmtH(s.lastGapH)} · 최대 간격 {fmtH(s.maxGapH)}<br />누락 {s.miss} · 지연 {s.late} · 재전송 {s.retry}
        {s.onTime != null && <><br />제때 수신 {s.onTime}%</>}</div>}>
        <span style={{ fontSize: 11, color: gray[500], fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          최대 <b style={{ color: warnGap(s.maxGapH) ? '#b91c1c' : gray[700] }}>{fmtS(s.maxGapH)}</b>
          {s.miss > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}> · 누락 {s.miss}</span>}
          {s.late > 0 && <span style={{ color: '#b45309', fontWeight: 700 }}> · 지연 {s.late}</span>}
        </span>
      </Tooltip>
    </div>
  );
};

export const ReceptionLegend: React.FC = () => (
  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 11.5, color: gray[600] }}>
    <span style={{ color: gray[500] }}>최근 수신(14일)</span>
    {(['ok', 'late', 'retry', 'miss'] as const).map(k => (
      <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <span style={{ width: 8, height: 11, background: CELL[k].bg, border: `1.5px solid ${CELL[k].border}`, borderRadius: 2, boxSizing: 'border-box' }} />{CELL[k].label}
      </span>))}
  </span>
);

/* 장치 이상 판정 — 장치 대장(행 강조 · '이상만' 필터 · 이유 글자)과 현황('확인 필요')이 같은 함수를 쓴다.
   두 화면의 숫자가 달랐다(현황 1 · 장치 대장 5, 2026-11-04 검토) — 판정은 여기 한 곳.
   심각: 미수신 · 배터리 교체 · 최근 업로드가 2주기 넘게 없음 / 주의: 수신 지연 · 14일 내 누락 · 지연 · 배터리 주의 · 신호 약함 · 설정 반영 실패 */
export type Issue = { lv: 'crit' | 'warn' | null; reasons: string[] };
export const devIssues = (d: any, rx: RxSummary | null): Issue => {
  if (!d.is_active) return { lv: null, reasons: [] };
  const crit: string[] = [], warn: string[] = [];
  if (d.level === 'CRIT') crit.push('미수신');
  else if (d.level === 'WARN') warn.push('수신 지연');
  const bg = batteryGrade(d.last_battery_v)?.key;
  if (bg === 'bad') crit.push('배터리 교체'); else if (bg === 'warn') warn.push('배터리 주의');
  if (signalGrade(d.last_signal_dbm)?.key === 'weak') warn.push('신호 약함');
  if (rx?.miss) warn.push(`누락 ${rx.miss}`);
  if (rx?.late) warn.push(`지연 ${rx.late}`);
  if (d.cfg_status === 'failed') warn.push('설정 반영 실패');
  return { lv: crit.length ? 'crit' : warn.length ? 'warn' : null, reasons: [...crit, ...warn] };
};

